import { memo, useCallback, useContext, useMemo } from 'react'
import clsx from 'clsx'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import SolidBedIcon from 'LuxKit/Icons/solid/SolidBedIcon'
import Image from 'components/Common/Image'
import NestableClickable from 'components/Common/NestableClickable'
import { chatMapCardContainer, CHAT_MAP_CARD_IMAGE_SIZE } from './chatMapCardLayout'
import NumberRating from 'components/Common/NumberRating'
import Group from 'components/utils/Group'
import noop from 'lib/function/noop'
import CurrencyContext from 'contexts/currencyContext'
import type { ExploreOffer } from 'tripPlanner/api/explore/types'
import type { TripItineraryData } from 'tripPlanner/api/types'
import AddHotelToTripButton from 'tripPlanner/components/AddToTrip/AddHotelToTripButton'
import AddToTripAction from 'tripPlanner/components/GenericDrawer/AddToTripAction'
import PriceDetails from 'tripPlanner/components/ItemOfferPrice/PriceDetails'
import {
  displayPricingFromDTO,
  getAccommodationUnavailableLabel,
  includesTaxesAndFees,
  isTotalMode,
  pricingUnavailableReason,
} from 'tripPlanner/utils/accommodationPricingDisplay'

const CardContainer = styled(NestableClickable)`
  ${chatMapCardContainer}
`

const ImageSection = styled.div`
  position: relative;
  width: ${rem(CHAT_MAP_CARD_IMAGE_SIZE)};
  min-width: ${rem(CHAT_MAP_CARD_IMAGE_SIZE)};
  min-height: ${rem(CHAT_MAP_CARD_IMAGE_SIZE)};
  flex-shrink: 0;
`

/*
  Takes the image out of flow so its intrinsic aspect ratio can't drive the card
  height - it just fills the image column and object-fit crops it. This, not the
  card height, is what stops a portrait photo stretching the row.
*/
const ImageFill = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
`

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  display: flex;
  align-items: center;
  justify-content: center;
`

const AddButtonContainer = styled.div`
  position: absolute;
  top: ${rem(4)};
  inset-inline-end: ${rem(4)};
`

const TitleHeading = styled(Heading)`
  flex: 1;
  min-width: 0;
`

const ContentSection = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${rem(8)} ${rem(12)};
  gap: ${rem(8)};
`

interface Props {
  offer: ExploreOffer
  itineraryData?: TripItineraryData
  onSendMessage: (message: string) => void
  onViewOffer: (data: { offerId: string; offerType?: string }) => void
  onHover?: (offerId: string | undefined) => void
  isHighlighted?: boolean
}

function ChatMapOfferCard({
  offer,
  itineraryData,
  onSendMessage,
  onViewOffer,
  onHover = noop,
  isHighlighted,
}: Props) {
  const intl = useIntl()
  const currentCurrency = useContext(CurrencyContext)

  const fromLabel = intl.formatMessage({
    id: 'dfcwDcH90Uz5YCe5kG37',
    defaultMessage: 'From',
    description: 'Price caption prefix when no night count is available on the map offer card',
  })
  const viewLabel = intl.formatMessage({
    id: 'Oki-KXseqCJeFJ3nRlAT',
    defaultMessage: 'View',
    description: 'Button label to view offer details on the map offer card',
  })
  const cardLabel = intl.formatMessage({
    id: 'o0tAKjn_S-BFV5XqJqKu',
    defaultMessage: 'View {offerName}',
    description: 'Accessible label for an offer card on the map results drawer, which opens that offer',
  }, { offerName: offer.name })

  const isExperienceOffer = offer.offerType === 'experience'
  const hotelPricing = !isExperienceOffer ? offer.pricing : undefined
  const hotelDisplayPricing = hotelPricing ? displayPricingFromDTO(hotelPricing) : undefined
  const displayPrice = isExperienceOffer ? offer.price : hotelDisplayPricing?.price
  const luxPlusPrice = isExperienceOffer ? undefined : hotelDisplayPricing?.luxPlusPrice
  const displayCurrency = hotelPricing?.currencyCode ?? currentCurrency
  const unavailableReason = pricingUnavailableReason(hotelPricing)
  const unavailableLabel = unavailableReason
    ? getAccommodationUnavailableLabel(unavailableReason, intl)
    : undefined

  const priceCaption = useMemo(() => {
    if (hotelPricing && isTotalMode(hotelPricing.mode)) {
      return intl.formatMessage({
        id: 'LiuMNKf6qmtOyTePdved',
        defaultMessage: 'Total',
        description: 'Price caption for a selected-rate hotel total on the map offer card',
      })
    }
    const numberOfNights = hotelPricing?.durationNights ?? offer.numberOfNights
    if (numberOfNights) {
      return intl.formatMessage(
        {
          id: '9dgRz0i4IHL0Iu5ubDlb',
          defaultMessage: '{count, plural, one {# night} other {# nights}} from',
          description: 'Price caption showing number of nights on the map offer card',
        },
        { count: numberOfNights },
      )
    }
    return fromLabel
  }, [hotelPricing, offer.numberOfNights, intl, fromLabel])

  const handleMouseEnter = useCallback(() => {
    onHover(offer.id)
  }, [offer.id, onHover])

  const handleMouseLeave = useCallback(() => {
    onHover(undefined)
  }, [onHover])

  const handleViewClick = useCallback(() => {
    onViewOffer({ offerId: offer.id, offerType: offer.offerType })
  }, [offer.id, offer.offerType, onViewOffer])

  return (
    <CardContainer
      data-testid="chat-map-offer-card"
      className={clsx({ highlighted: isHighlighted })}
      aria-label={cardLabel}
      onClick={handleViewClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <ImageSection>
        <ImageFill>
          {offer.imageIds[0] && (
            <Image
              id={offer.imageIds[0]}
              alt={offer.name}
              width={CHAT_MAP_CARD_IMAGE_SIZE}
              height={CHAT_MAP_CARD_IMAGE_SIZE}
              gravity="auto"
              fit="center"
              loading="lazy"
            />
          )}
          {!offer.imageIds[0] && (
            <ImagePlaceholder>
              <SolidBedIcon size="M" colour="light-foreground-alt" />
            </ImagePlaceholder>
          )}
        </ImageFill>
        {isExperienceOffer && itineraryData && (
          <AddButtonContainer>
            <AddToTripAction
              itineraryData={itineraryData}
              onSendMessage={onSendMessage}
              placeName={offer.name}
              offerId={offer.id}
              offerType={offer.offerType}
              coordinates={offer.coordinates}
              variant="filled"
            />
          </AddButtonContainer>
        )}
        {!isExperienceOffer && (
          <AddButtonContainer>
            <AddHotelToTripButton
              itineraryData={itineraryData}
              hotelName={offer.name}
              offerId={offer.id}
              offerType={offer.offerType}
              hotelCity={offer.location}
              onSendMessage={onSendMessage}
            />
          </AddButtonContainer>
        )}
      </ImageSection>

      <ContentSection>
        <Group direction="vertical" gap={4}>
          <Group direction="horizontal" gap={8} verticalAlign="start" horizontalAlign="space-between">
            <TitleHeading data-testid="chat-map-offer-card-title" variant="heading6" lineClamp={2}>{offer.name}</TitleHeading>
            {offer.rating !== undefined && (
              <Group direction="horizontal" shrink={false}>
                <NumberRating rating={offer.rating} variant="xs" onlyNumber />
              </Group>
            )}
          </Group>
          {offer.description && (
            <BodyText data-testid="chat-map-offer-card-summary" variant="small" colour="light-foreground-alt" lineClamp={2}>
              {offer.description}
            </BodyText>
          )}
          {!offer.description && offer.location && (
            <BodyText data-testid="chat-map-offer-card-summary" variant="small" colour="light-foreground-alt" lineClamp={2}>
              {offer.location}
            </BodyText>
          )}
        </Group>

        <Group direction="horizontal" gap={8} verticalAlign="center" horizontalAlign="space-between" shrink={false}>
          <div>
            {displayPrice !== undefined && (
              <PriceDetails
                price={displayPrice}
                luxPlusPrice={luxPlusPrice}
                currencyCode={displayCurrency}
                priceLabel={priceCaption}
                totalLabel={offer.pricePer}
                showTaxesAndFees={hotelPricing ? includesTaxesAndFees(hotelPricing) : false}
              />
            )}
            {displayPrice === undefined && unavailableLabel && (
              <BodyText variant="small" weight="bold">
                {unavailableLabel}
              </BodyText>
            )}
          </div>
          <TextButton
            kind={isHighlighted ? 'primary' : 'secondary'}
            size="small"
            tabIndex={-1}
            onClick={handleViewClick}
          >
            {viewLabel}
          </TextButton>
        </Group>
      </ContentSection>
    </CardContainer>
  )
}

export default memo(ChatMapOfferCard)
